import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type ScreenReaderInstructions,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { MouseEvent as ReactMouseEvent } from 'react';

import type { LocaleFormattingTranslate } from './locale-formatting';

/**
 * Accessible drag-and-drop defaults.
 *
 * Boilerplate plan §3.F is explicit that the keyboard sensor and
 * `screenReaderInstructions` go in with the *first* drag implementation rather
 * than as a later pass: `FR-EXP-019`, `FR-EXP-020` and `FR-EXP-023` are the
 * accessibility line `00-stack-recommendation.md` chose to hold as a
 * differentiator, cross-cutting principle 6 says no action is mouse-only, and
 * retrofitting this is the expensive path the spec warns about.
 *
 * No drag is implemented here — that is feature work. What is here is the
 * configuration a drag needs, so that the first one to be written inherits it
 * instead of having to remember it.
 *
 * WHICH DND-KIT (§2.4). dnd-kit publishes two API lines at once. This project
 * is on **`@dnd-kit/core` v6 with `@dnd-kit/sortable` v10** — the line
 * documented as *Legacy* on dndkit.com. It is the mature one and it is what
 * every example below assumes. The newer `@dnd-kit/react` / `@dnd-kit/dom`
 * packages are a different, incompatible API; do not mix idioms between them,
 * and check which tree a snippet came from before pasting it.
 */

/**
 * FE-80.1 — a FUNCTION, where this was a module constant.
 *
 * The instruction block is the first thing a screen-reader member hears about
 * the whole drag affordance, and a module constant cannot call `t`. Both
 * exports below take `t` as a required first parameter for the reason
 * `lib/card-title.ts` records: a defaulted translator lets one call site fall
 * through to a default that looks correct, and only a required one makes `tsc`
 * enumerate the callers.
 */
export function dragInstructions(t: LocaleFormattingTranslate): ScreenReaderInstructions {
  return { draggable: t('dnd.instructions') };
}

/**
 * Spoken feedback during a drag.
 *
 * `describe` turns a dragged id into something a person can hear — a card
 * title, a list name. Announcing an opaque id is technically compliant and
 * practically useless, so the resolver is required rather than optional.
 *
 * ⚠ SIX keys for six sentences, and not one sentence plus conditional
 * fragments. `onDragOver` and `onDragEnd` each have an over/no-over pair whose
 * arms take DIFFERENT slots — the `over` arm needs `{target}` and the bare arm
 * has no target to name — so a shared key would have to be given a value for a
 * slot it cannot fill, and `interfaceMessage` THROWS on that rather than
 * rendering the wrong string. The pairs are pinned by fixtures that reach both
 * arms.
 */
export function dragAnnouncements(
  t: LocaleFormattingTranslate,
  describe: (id: string) => string,
): Announcements {
  return {
    onDragStart: ({ active }) => t('dnd.pickedUp', { item: describe(String(active.id)) }),
    onDragOver: ({ active, over }) =>
      over
        ? t('dnd.over', {
            item: describe(String(active.id)),
            target: describe(String(over.id)),
          })
        : t('dnd.notOver', { item: describe(String(active.id)) }),
    onDragEnd: ({ active, over }) =>
      over
        ? t('dnd.dropped', {
            item: describe(String(active.id)),
            target: describe(String(over.id)),
          })
        : t('dnd.droppedOutside', { item: describe(String(active.id)) }),
    onDragCancel: ({ active }) =>
      t('dnd.cancelled', { item: describe(String(active.id)) }),
  };
}

/**
 * `FE-40.7` (`FR-VIEW-033` AC2) — THE MOUSE/TOUCH SPLIT, AND WHY IT IS A SPLIT
 * RATHER THAN A THIRD SENSOR ADDED BESIDE THE POINTER ONE.
 *
 * ⚠ **A `TouchSensor` added next to a `PointerSensor` is inert at any array
 * position.** This is the whole reason `FR-VIEW-033` had no touch drag for
 * three chunks, and it is not guessable from the outside — nothing goes red,
 * the sensor is simply never asked. Verified in `@dnd-kit/core` **6.3.1**
 * `dist/core.esm.js`, read rather than recalled:
 *
 *   - `PointerSensor.activators` is `[{ eventName: 'onPointerDown' }]`, and its
 *     handler returns `false` **only** for `!event.isPrimary || event.button !== 0`.
 *     A first finger is primary with button 0, so it returns `true`:
 *     `PointerSensor` **claims touch input**.
 *   - `bindActivatorToSensorInstantiator` early-returns on
 *     `activeRef.current !== null || nativeEvent.dndKit`, and a successful
 *     activation sets both.
 *   - Chromium fires `pointerdown` **before** `touchstart`.
 *
 * So the pointer sensor wins the race and `TouchSensor`'s `onTouchStart` is
 * dropped on the floor. The list must **stop using a `PointerSensor` for touch
 * at all** — which is exactly the `MouseSensor` + `TouchSensor` split dnd-kit
 * documents.
 *
 * ⚠ **What a `PointerSensor` did on a phone, precisely** — because two shipped
 * comments (`CardTile.tsx`, `ListHeader.tsx`) record this wrongly, and the wrong
 * cause names the wrong fix. It did **not** fail to claim the gesture. It
 * claimed it and was then killed by the browser's own scroll: `pointermove`
 * during a native pan is **non-cancelable**, so `AbstractPointerSensor.handleMove`'s
 * `if (event.cancelable) event.preventDefault()` is a no-op and `pointercancel`
 * lands in `handleCancel`. The fix was never `touch-action: none`.
 *
 * ⚠ **`touch-none` is therefore NOT needed on the card front, and that is a
 * measured claim rather than a hope.** `TouchSensor.setup()` installs a
 * window-level `touchmove` listener with `{ capture: false, passive: false }`
 * for the express purpose of making `preventDefault()` work in dynamically
 * added handlers. Suppression is bought by the sensor **at drag time** instead
 * of by CSS **at rest** — so it costs list scrolling only *during* a drag,
 * rather than always. `CardTile.tsx`'s comment carries the trade-off in full.
 *
 * THE TRADE-OFF, NAMED. This takes the activation-delay side:
 *   - it SERVES `FR-VIEW-033` AC1 — the strip still swipes between lists and the
 *     card stack still scrolls under a finger, because nothing is prevented
 *     until the delay elapses;
 *   - it COSTS `FR-VIEW-033` AC2 a quarter-second — the card lifts 250ms after
 *     the finger lands rather than on contact.
 * `touch-none` on the card front is the other side: no latency, and no list
 * scrolling from a swipe that begins on any card. A card front fills almost the
 * full height of a scrollable list, so that side loses.
 *
 * ⚠ **The keyboard sensor below is untouched, byte for byte.** `FE-04.4`'s
 * Space-picks-up / Enter-activates narrowing is not reopened here.
 */

/**
 * ⚠ `MouseSensor` with `PointerSensor`'s BUTTON SEMANTICS PUT BACK, and it is
 * ten lines to avoid a silent regression rather than an embellishment.
 *
 * `MouseSensor.activators`' shipped handler returns `false` only for
 * `event.button === MouseButton.RightClick` (`2`). `PointerSensor` excluded
 * every non-primary pointer **and every non-zero button**. Swapping one for the
 * other bare therefore widens what can start a drag: a **middle-click on a card
 * front** — which is an `<a href>`, so "open in a new tab" — would enter a
 * pending drag, and 6px of drift with button 1 held would start a real one while
 * the browser opens the tab anyway on `auxclick`. `CardTile.tsx` calls the card
 * front *"the highest-risk element in the chunk"*; this keeps the desktop path
 * byte-identical to what shipped.
 */
class PrimaryMouseSensor extends MouseSensor {
  static override activators = [
    {
      eventName: 'onMouseDown' as const,
      handler: ({ nativeEvent: event }: ReactMouseEvent): boolean => event.button === 0,
    },
  ];
}

/**
 * The mouse activation distance, hoisted to a module constant so that
 * `ListHeader.tsx:451-455` ("this file must not remove or lower it") has a
 * single value to point at, and so `useSensor`'s `useMemo` — which keys on
 * `[sensor, options]` — is not defeated by a fresh object literal every render.
 *
 * With no constraint at all, dnd-kit claims the mouse on `mousedown` and a plain
 * click to open a card registers as a zero-distance drag instead. 6px is what
 * keeps a card clickable, and it is unchanged from the `PointerSensor` era.
 */
export const MOUSE_ACTIVATION = { distance: 6 } as const;

/**
 * The touch long press (`FR-VIEW-033` AC2).
 *
 * `delay: 250` — long enough that a swipe to scroll is over well before it
 * elapses, short enough not to feel broken; dnd-kit's own documented
 * `TouchSensor` recipe value.
 *
 * `tolerance: 5` — checked in `AbstractPointerSensor.handleMove`, where movement
 * beyond it **during** the delay calls `handleCancel()`. It is what makes a pan
 * a pan: 5px lets a resting finger jitter without cancelling, and cancels the
 * instant the gesture is really a scroll. Because `handleStart` never ran, no
 * click-stopper is installed either — so a plain tap still opens the card.
 */
export const TOUCH_ACTIVATION = { delay: 250, tolerance: 5 } as const;

/*
 * ⚠ ALL THREE OPTION OBJECTS ARE MODULE CONSTANTS, AND THAT IS LOAD-BEARING
 * RATHER THAN TIDY. `useSensor` memoises on `[sensor, options]` and `useSensors`
 * on the spread of the descriptors it is given, so a fresh object LITERAL at any
 * one of the three call sites defeats both and hands `DndContext` a new
 * `sensors` array on every render of the board face.
 *
 * Hoisting only the inner `activationConstraint` values is NOT enough — the
 * surrounding `{ activationConstraint: … }` wrapper is itself a fresh literal.
 * That was this chunk's first attempt, and `dnd.test.ts`'s stability case caught
 * it; the comment claiming the fix was already wrong before the test ran.
 */

/** Options for the mouse sensor. See `MOUSE_ACTIVATION`. */
const MOUSE_SENSOR_OPTIONS = { activationConstraint: MOUSE_ACTIVATION };

/** Options for the touch sensor. See `TOUCH_ACTIVATION`. */
const TOUCH_SENSOR_OPTIONS = { activationConstraint: TOUCH_ACTIVATION };

/**
 * Options for the keyboard sensor — unchanged by `FE-40.7`'s sensor split
 * except for being hoisted out of the call.
 *
 * ⚠ **Space picks up; Enter activates.** Narrowed by the parent in chunk
 * `C3`, and it is the decision that closes `FE-04.4` rather than a
 * preference.
 *
 * dnd-kit's default `keyboardCodes.start` is `['Space', 'Enter']`. That
 * is harmless for a list, whose drag handle is a `<button>` where the two
 * keys are equivalent anyway — and wrong for a card, whose whole front is
 * an `<a href>` that a member drags by grabbing anywhere on it
 * (`FR-BOARD-025`). At the default, **Enter on a focused card starts a
 * drag instead of opening the card**, which is the one thing Enter means
 * on a link. No list test could have caught it.
 *
 * Verified before narrowing: nothing in the C2 suite or in
 * `e2e/FR-BOARD-011.spec.ts` asserts that Enter starts a list drag — both
 * the unit tests and the browser spec use Space throughout — so this
 * costs the shipped list drag nothing and makes the rule uniform across
 * the product.
 */
const KEYBOARD_SENSOR_OPTIONS = {
  // Moves the keyboard cursor between sortable items rather than by pixels.
  coordinateGetter: sortableKeyboardCoordinates,
  keyboardCodes: {
    start: ['Space'],
    cancel: ['Escape'],
    end: ['Space', 'Enter'],
  },
};

/**
 * The sensors every `DndContext` in this app should use.
 */
export function useMoltSensors(): ReturnType<typeof useSensors> {
  return useSensors(
    useSensor(PrimaryMouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );
}
